import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { AuthApi } from "../services/api"

interface User {
	id: string
	name?: string
	email?: string
	phone?: string
	firstName?: string
	lastName?: string
	middleName?: string
	birthDate?: string
	address?: string
	role?: string
	emailVerified?: boolean
	emailVerifiedAt?: string | null
}

/**
 * Hook для получения данных текущего пользователя с кэшированием
 *
 * Преимущества:
 * - Автоматическое кэширование (нет дублирующихся запросов)
 * - Общий кэш для всех компонентов
 * - Автоматическое обновление при изменениях
 *
 * @example
 * ```typescript
 * const { data: user, isLoading, error } = useAuthUser()
 *
 * if (isLoading) return <Skeleton />
 * if (!user) return <LoginForm />
 * return <div>Привет, {user.name}!</div>
 * ```
 */
export const useAuthUser = () => {
	return useQuery<User | null>({
		queryKey: ["auth", "me"],
		queryFn: async () => {
			// Пробуем получить данные пользователя
			// Если токен есть в памяти - используем его
			// Если нет - пробуем refresh через httpOnly cookie
			try {
				return (await AuthApi.me()) as User
			} catch (error) {
				// Если 401 - interceptor автоматически попытается обновить токен через refresh cookie
				// Если refresh не удался, interceptor очистит токен и перенаправит на логин
				// Здесь просто возвращаем null, чтобы показать, что пользователь не авторизован
				if ((error as any)?.response?.status === 401) {
					return null
				}
				throw error
			}
		},
		staleTime: 5 * 60 * 1000, // 5 минут - данные свежие
		gcTime: 10 * 60 * 1000, // 10 минут - храним в кэше
		retry: (failureCount, error: any) => {
			// Не повторяем при 401 - interceptor обработает это
			if (error?.response?.status === 401) {
				return false
			}
			// Повторяем другие ошибки максимум 1 раз
			return failureCount < 1
		},
		refetchOnMount: true, // Перезапрашиваем при монтировании для проверки актуальности
		refetchOnWindowFocus: true, // Обновляем при фокусе окна для синхронизации между вкладками
		refetchOnReconnect: true, // Обновляем при восстановлении сети
	})
}

/**
 * Hook для обновления профиля
 */
export const useUpdateProfile = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: {
			name?: string
			email?: string
			phone?: string | null
			firstName?: string | null
			lastName?: string | null
			middleName?: string | null
			birthDate?: string | null
			address?: string | null
		}) => AuthApi.updateProfile(data),
		onSuccess: (updatedUser) => {
			// Обновляем кэш автоматически
			queryClient.setQueryData(["auth", "me"], updatedUser)
		},
	})
}

/**
 * Hook для отправки письма подтверждения email
 */
export const useSendEmailVerification = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: () => AuthApi.sendEmailVerification(),
		onSuccess: async () => {
			// Можем обновить @me (например, если backend вернёт изменения)
			await queryClient.invalidateQueries({ queryKey: ["auth", "me"] })
		},
	})
}

/**
 * Hook для логина с автоматическим обновлением кэша
 */
export const useLogin = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ email, password }: { email: string; password: string }) =>
			AuthApi.login(email, password),
		onSuccess: async () => {
			// Инвалидируем кэш - React Query автоматически обновит данные при следующем использовании
			await queryClient.invalidateQueries({ queryKey: ["auth", "me"] })
		},
	})
}

/**
 * Hook для регистрации с автоматическим обновлением кэша
 */
export const useRegister = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({
			name,
			email,
			password,
			phone,
		}: {
			name: string
			email: string
			password: string
			phone: string
		}) => AuthApi.register(name, email, password, phone),
		onSuccess: async () => {
			// Инвалидируем кэш - React Query автоматически обновит данные при следующем использовании
			await queryClient.invalidateQueries({ queryKey: ["auth", "me"] })
		},
	})
}

/**
 * Hook для выхода
 */
export const useLogout = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: () => AuthApi.logout(),
		onSuccess: () => {
			// Очищаем кэш пользователя
			queryClient.setQueryData(["auth", "me"], null)
			queryClient.clear()
			
			// Очищаем сохранённые данные формы полиса
			localStorage.removeItem('blue-card-policy-form')
		},
	})
}
